// Host portrait card — renders a real photo when provided, falls back to MS monogram.
import { Mic, MapPin, Calendar } from "lucide-react";

export default function HostCard({
  name = "Manuel Santana",
  role = "Host · Founder",
  photo = null,
  location = "NYC · Boston",
}) {
  const initials = name
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("");

  return (
    <div
      data-testid="host-card"
      className="relative w-full aspect-[4/5] bg-[#0A0A0A] border border-[#262626] overflow-hidden"
    >
      {photo ? (
        <>
          {/* Real photo */}
          <img
            src={photo}
            alt={name}
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Red+blue color wash to match brand */}
          <div
            className="absolute inset-0 mix-blend-multiply opacity-40 pointer-events-none"
            style={{
              background:
                "linear-gradient(135deg, rgba(225,6,0,0.5) 0%, transparent 45%, transparent 55%, rgba(30,111,224,0.5) 100%)",
            }}
          />
          <div className="absolute inset-0 bg-black/15 pointer-events-none" />
        </>
      ) : (
        <>
          {/* Split background: half red, half blue */}
          <div className="absolute inset-0 grid grid-cols-2">
            <div
              className="opacity-15"
              style={{ background: "radial-gradient(circle at 30% 30%, #E10600 0%, transparent 70%)" }}
            />
            <div
              className="opacity-15"
              style={{ background: "radial-gradient(circle at 70% 70%, #1E6FE0 0%, transparent 70%)" }}
            />
          </div>
          {/* Halftone */}
          <div
            className="absolute inset-0 opacity-25 pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle, rgba(255,255,255,0.4) 1px, transparent 1.4px)",
              backgroundSize: "12px 12px",
            }}
          />
          {/* Big initials */}
          <div className="absolute inset-0 flex items-center justify-center">
            <span
              aria-hidden
              className="font-display leading-none text-white"
              style={{ fontSize: "clamp(8rem, 22vw, 16rem)" }}
            >
              {initials}
            </span>
          </div>
        </>
      )}

      {/* corner accents (always shown) */}
      <div className="absolute top-0 left-0 h-1 w-12 bg-[#E10600] z-10" />
      <div className="absolute top-0 left-0 w-1 h-12 bg-[#E10600] z-10" />
      <div className="absolute bottom-0 right-0 h-1 w-12 bg-[#1E6FE0] z-10" />
      <div className="absolute bottom-0 right-0 w-1 h-12 bg-[#1E6FE0] z-10" />

      {/* Mic + name overlay top-left */}
      <div className="absolute top-4 left-4 z-10 flex items-center gap-2 bg-black/65 backdrop-blur-sm px-2 py-1.5 border border-[#262626]">
        <span className="w-7 h-7 bg-[#E10600] flex items-center justify-center">
          <Mic className="w-3.5 h-3.5 text-white" />
        </span>
        <div>
          <p className="text-[9px] tracking-[0.2em] uppercase font-mono text-zinc-400 leading-none">
            Host
          </p>
          <p className="font-display text-base uppercase leading-none text-white">
            {name}
          </p>
        </div>
      </div>

      {/* Bottom meta bar */}
      <div className="absolute bottom-0 left-0 right-0 z-10 bg-black/90 backdrop-blur-sm border-t border-[#262626] p-4 flex items-center justify-between">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-[#E10600]">
            {role}
          </p>
          <p className="mt-1 text-xs text-zinc-300 flex items-center gap-1">
            <MapPin className="w-3 h-3 text-[#1E6FE0]" />
            {location}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-[#1E6FE0]">
            On Air
          </p>
          <p className="mt-1 text-xs text-zinc-300 flex items-center gap-1 justify-end">
            <Calendar className="w-3 h-3 text-[#E10600]" />
            Mon · 7PM EST
          </p>
        </div>
      </div>
    </div>
  );
}
